import type { PublicVesselSummary } from "../shared/contracts";
import {
  RIVER_MOUTH_S_METERS,
  courseSceneVector,
  inboundRouteTangent,
  routeLocation,
  type RiverPoint,
  type RouteLocation,
} from "../shared/river-model";

/** +1 faces toward Brickell along the route (upriver); -1 faces seaward. */
export type TravelSign = -1 | 1;

/** Along-route movement below this speed is treated as jitter, not travel. */
export const OBSERVED_TRAVEL_SIGN_METERS_PER_SECOND = 0.3;
/** Reported course over ground is only trusted as a heading above this speed. */
export const COURSE_HEADING_MIN_KNOTS = 1;
/** |cos| between course and route below this is "crossing", not along-route evidence. */
const COURSE_ALIGNMENT_THRESHOLD = 0.3;
/** Hulls stay route-aligned this close to the river mouth junction. */
const BAY_HEADING_BLEND_START_METERS = 80;
/** Beyond this distance seaward of the mouth the hull follows its reported course. */
const BAY_HEADING_BLEND_FULL_METERS = 450;

export type HeadingVessel = Pick<
  PublicVesselSummary,
  "corridor" | "direction" | "bridgePhase" | "sMeters" | "corridorProgress" | "speedKnots" | "courseDegrees"
>;

export interface TravelSignEvidence {
  /** Signed along-route velocity from successive fixes; positive is toward Brickell. */
  observedVelocityMetersPerSecond?: number | undefined;
  /** The facing already on screen, kept unless stronger evidence contradicts it. */
  previousSign?: TravelSign | null | undefined;
}

/**
 * Which way the bow points along the route. Physical evidence wins over the
 * derived direction flag: observed along-route movement first, then the
 * reported course over ground while the vessel is actually moving. Without
 * either, a facing already on screen is kept, so a noisy flag cannot spin a
 * slow hull around; only then do the published direction and bridge phase
 * apply. Zero means nothing is known; callers choose a default.
 */
export function vesselTravelSign(
  vessel: HeadingVessel,
  along: RiverPoint,
  evidence: TravelSignEvidence = {},
): TravelSign | 0 {
  const observed = evidence.observedVelocityMetersPerSecond;
  if (
    observed !== undefined &&
    Number.isFinite(observed) &&
    Math.abs(observed) >= OBSERVED_TRAVEL_SIGN_METERS_PER_SECOND
  ) {
    return observed > 0 ? 1 : -1;
  }
  const course = courseSceneVector(vessel.courseDegrees);
  const speedKnots = vessel.speedKnots ?? 0;
  if (course && Number.isFinite(speedKnots) && speedKnots >= COURSE_HEADING_MIN_KNOTS) {
    const alignment = course.x * along.x + course.z * along.z;
    if (Math.abs(alignment) >= COURSE_ALIGNMENT_THRESHOLD) return alignment > 0 ? 1 : -1;
  }
  if (evidence.previousSign) return evidence.previousSign;
  if (vessel.direction === "inbound") return 1;
  if (vessel.direction === "outbound") return -1;
  if (vessel.bridgePhase === "approaching") return vessel.sMeters <= 0 ? 1 : -1;
  if (vessel.bridgePhase === "clearing") return vessel.sMeters >= 0 ? 1 : -1;
  return 0;
}

/**
 * Sprite convention for the flat atlas and DOM lists: masters face
 * screen-right (seaward), so a hull travelling toward Brickell is mirrored.
 */
export function vesselFacesBrickell(vessel: HeadingVessel): boolean {
  const location = routeLocation(vessel.corridor, vessel.sMeters, vessel.corridorProgress);
  return vesselTravelSign(vessel, inboundRouteTangent(location)) > 0;
}

/**
 * The unit vector the bow points along in scene space. Inside the river the
 * hull stays locked to the mapped channel tangent. In open bay water the
 * authored approach branches are only coarse normalisations, so a moving
 * hull rotates toward its reported course, easing back onto the branch
 * tangent as it nears the river mouth junction.
 */
export function vesselHeading(
  vessel: HeadingVessel,
  location: RouteLocation,
  renderedSMeters: number,
  sign: TravelSign | 0,
  idle: boolean,
): RiverPoint {
  const along = inboundRouteTangent(location);
  const base = sign < 0 ? { x: -along.x, z: -along.z } : along;
  if (idle || location.route === "miami_river") return base;
  const course = courseSceneVector(vessel.courseDegrees);
  const speedKnots = vessel.speedKnots ?? 0;
  if (!course || !Number.isFinite(speedKnots) || speedKnots < COURSE_HEADING_MIN_KNOTS) return base;
  const seawardMeters = RIVER_MOUTH_S_METERS - renderedSMeters;
  const weight = smoothstep(BAY_HEADING_BLEND_START_METERS, BAY_HEADING_BLEND_FULL_METERS, seawardMeters);
  if (weight <= 0) return base;
  const baseAngle = Math.atan2(base.z, base.x);
  const courseAngle = Math.atan2(course.z, course.x);
  const angle = baseAngle + wrapAngle(courseAngle - baseAngle) * weight;
  return { x: Math.cos(angle), z: Math.sin(angle) };
}

export function wrapAngle(radians: number): number {
  let wrapped = radians % (Math.PI * 2);
  if (wrapped > Math.PI) wrapped -= Math.PI * 2;
  if (wrapped < -Math.PI) wrapped += Math.PI * 2;
  return wrapped;
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const amount = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)));
  return amount * amount * (3 - 2 * amount);
}
